/**
 * 상품 → 지점/차종 집계 (설계 §2-1) — **순수 함수**.
 *
 * `useStationHubData` 훅이 useMemo 로 감싸 쓰고, 테스트는 이 파일을 직접 부른다
 * (jsdom 없이 집계 규칙만 검증할 수 있게 훅에서 분리했다).
 */

import type { Product } from "@/types";
import type {
  HubProduct,
  ModelSummary,
  StationDetail,
  StationHubDataset,
  StationSummary,
} from "@/types/mobility";
import { textCompare } from "@/lib/utils/text-compare";
import type { SupportedCurrency } from "@/lib/utils/currency";
import {
  bikeClassOf,
  bikeClassOrder,
  getBikeClass,
  modelKeyOf,
  type BikeClassKey,
} from "./bike-classes";
import { representativeDailyPrice } from "./pricing";
import { EMPTY_FACETS, type ProductFacets } from "./bike-facets";
import { resolveBikeMaker } from "./bike-makers";
import { resolvePartySize } from "./camp-party";

export interface StationHubAggregate {
  stations: StationSummary[];
  stationsById: Map<string, StationSummary>;
  models: ModelSummary[];
  /**
   * 상품 id → 필터 축 값(배기량 클래스 + 제원). 허브 필터(`matchesAxes`)와 행의
   * 제조사·타입·변속 표기가 읽는다. 상품 폴백 경로는 제원이 없어 클래스만 채운다.
   */
  facets: Map<string, ProductFacets>;
  /** 택배(국내 배송)로도 받을 수 있는 상품 id — 캠핑 [장비] 탭이 지도 범위 밖이어도 남긴다. */
  shippable: Set<string>;
}

function isFiniteCoord(n: unknown): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * 상품들의 `branches[]` 를 뒤집어 지점별 상품 목록을 만든다.
 * 좌표가 없는 지점은 버린다(지도 핀을 그릴 수 없고 허브의 모든 화면이 거리순이다).
 *
 * 정렬: 상품 수 내림차순 → 지점 id (결정적). 거리순 정렬은 지도 중심을 아는
 * 화면 쪽 책임이다.
 */
export function aggregateStations(products: Product[]): StationSummary[] {
  const byId = new Map<string, StationSummary>();
  const classSets = new Map<string, Set<BikeClassKey>>();

  for (const product of products ?? []) {
    const price = representativeDailyPrice(product);
    const currency = product.currency as SupportedCurrency | undefined;
    const image = product.images?.[0] ?? null;
    const klass = bikeClassOf(product);

    for (const branch of product.branches ?? []) {
      if (!branch?.id) continue;
      if (!isFiniteCoord(branch.latitude) || !isFiniteCoord(branch.longitude)) {
        continue;
      }
      let station = byId.get(branch.id);
      if (!station) {
        station = {
          id: branch.id,
          lat: branch.latitude,
          lng: branch.longitude,
          city: branch.city ?? "",
          products: [],
          minPrice: null,
          classes: [],
          image,
        };
        byId.set(branch.id, station);
        classSets.set(branch.id, new Set<BikeClassKey>());
      }
      station.products.push(product);
      if (!station.city && branch.city) station.city = branch.city;
      if (!station.image && image) station.image = image;
      if (price != null && (station.minPrice == null || price < station.minPrice)) {
        station.minPrice = price;
        station.currency = currency;
      }
      if (klass !== "unknown") classSets.get(branch.id)!.add(klass);
    }
  }

  for (const [id, set] of classSets) {
    const station = byId.get(id);
    if (!station) continue;
    station.classes = [...set]
      .sort((a, b) => bikeClassOrder(a) - bikeClassOrder(b))
      .map((key) => getBikeClass(key)!)
      .filter(Boolean);
  }

  return [...byId.values()].sort(
    (a, b) => b.products.length - a.products.length || a.id.localeCompare(b.id),
  );
}

/**
 * 같은 `modelKeyOf` 키의 상품(인기/스탠다드 2개)을 한 모델로 묶는다.
 * 모델 대표가 = products 중 최저 1일가, 모델의 지점 = products 의 branches 합집합.
 *
 * 정렬: 클래스 순서(BIKE_CLASSES) → 대표가 오름차순 → 이름.
 */
export function aggregateModels(
  products: Product[],
  /** 이름 정렬 로케일 — 페이지 로케일(서버·브라우저 같은 순서) */
  locale = "en",
): ModelSummary[] {
  const compare = textCompare(locale);
  const byKey = new Map<string, ModelSummary>();

  for (const product of products ?? []) {
    const key = modelKeyOf(product);
    if (!key) continue;
    let model = byKey.get(key);
    if (!model) {
      model = {
        key,
        name: key,
        classKey: bikeClassOf(product),
        products: [],
        minPrice: null,
        stationIds: new Set<string>(),
        image: product.images?.[0] ?? null,
      };
      byKey.set(key, model);
    }
    model.products.push(product);
    // 클래스가 갈라진 데이터는 첫 번째로 확정된(=unknown 아닌) 값을 쓴다.
    if (model.classKey === "unknown") model.classKey = bikeClassOf(product);
    if (!model.image && product.images?.[0]) model.image = product.images[0];

    const price = representativeDailyPrice(product);
    if (price != null && (model.minPrice == null || price < model.minPrice)) {
      model.minPrice = price;
      model.currency = product.currency as SupportedCurrency | undefined;
    }
    for (const branch of product.branches ?? []) {
      if (branch?.id) model.stationIds.add(branch.id);
    }
  }

  const INF = Number.POSITIVE_INFINITY;
  for (const model of byKey.values()) {
    model.products.sort(
      (a, b) =>
        (representativeDailyPrice(a) ?? INF) - (representativeDailyPrice(b) ?? INF),
    );
  }

  return [...byKey.values()].sort(
    (a, b) =>
      bikeClassOrder(a.classKey) - bikeClassOrder(b.classKey) ||
      (a.minPrice ?? INF) - (b.minPrice ?? INF) ||
      compare(a.name, b.name),
  );
}

/** 지점 행·차종 시트 머리가 쓰는 상품 묶음 요약 — 필터 적용 후 상품 배열을 받는다. */
export interface ProductGroupSummary {
  /** 요금제 접미사를 뗀 모델 수(= 차종 시트의 행 수) */
  modelCount: number;
  /** 등장한 배기량 클래스(중복 제거·unknown 제외, BIKE_CLASSES 순서) */
  classKeys: BikeClassKey[];
  /** 최저 대표가(마크업 반영) */
  minPrice: number | null;
  currency?: SupportedCurrency;
}

export function summarizeProducts(
  products: readonly Product[],
  facetsOf: (productId: string) => ProductFacets | undefined,
): ProductGroupSummary {
  const models = new Set<string>();
  const classes = new Set<BikeClassKey>();
  let minPrice: number | null = null;
  let currency: SupportedCurrency | undefined;
  for (const product of products) {
    models.add(modelKeyOf(product) || product.id);
    const key = facetsOf(product.id)?.classKey ?? bikeClassOf(product);
    if (key !== "unknown") classes.add(key);
    const price = representativeDailyPrice(product);
    if (price != null && (minPrice == null || price < minPrice)) {
      minPrice = price;
      currency = product.currency as SupportedCurrency | undefined;
    }
  }
  return {
    modelCount: models.size,
    classKeys: [...classes].sort((a, b) => bikeClassOrder(a) - bikeClassOrder(b)),
    minPrice,
    currency,
  };
}

export function buildStationHubData(products: Product[], locale = "en"): StationHubAggregate {
  const stations = aggregateStations(products);
  return {
    stations,
    stationsById: new Map(stations.map((s) => [s.id, s])),
    models: aggregateModels(products, locale),
    // 상품 목록 경로에는 specifications 가 실려 오지 않는다 — 클래스 + 이름 기반 제조사,
    // 태그·이름 기반 캠핑 인원만.
    facets: new Map(
      (products ?? []).map((product) => [
        product.id,
        {
          ...EMPTY_FACETS,
          classKey: bikeClassOf(product),
          makerKey: resolveBikeMaker(product.name),
          partySize: resolvePartySize(product.tags, product.name),
        },
      ]),
    ),
    shippable: new Set(
      (products ?? [])
        .filter((product) =>
          product.usage_methods?.some((m) => m.method_type === "delivery" && m.enabled),
        )
        .map((product) => product.id),
    ),
  };
}

// ── 서버 데이터셋 모드 (lib/api/station-hub.ts) ─────────────────────────────

/**
 * 압축 행(`HubProduct`)을 화면이 읽는 `Product` 모양으로 되살린다.
 *
 * 허브 UI(ItemRow·ModelSheet·StationBikesSheet)는 이미 `Product` 를 읽고
 * `bikeClassOf`/`modelKeyOf`/`periodTotal` 을 부른다. 새 타입을 UI 까지
 * 밀어 넣는 대신 여기서 한 번 되살리면 **UI 코드가 한 줄도 바뀌지 않는다**.
 *
 * - `tags` 는 클래스 태그 한 줄로 복원 → `bikeClassOf` 가 그대로 동작
 * - `day_prices` 는 tiers 에서 복원 → `periodTotal` 이 기간 할인을 그대로 반영
 * - `markupApplied: true` — 서버에서 이미 올렸으므로 클라 경로가 두 번 올리지 않게
 */
export function hubProductToProduct(
  hub: HubProduct,
  assetBase: string,
  branches: Array<{ id: string; city: string; latitude: number; longitude: number }>,
  /** 기본 상품 카드의 지역 줄 — 지점의 시·도(state_province, 없으면 도시). 일반 검색과 같은 단위 */
  regions: string[] = [],
): Product {
  return {
    id: hub.id,
    partner_id: "",
    partner_name: hub.partnerName ?? undefined,
    partner_slug: hub.partnerSlug ?? null,
    name: hub.name,
    status: "approved",
    is_featured: false,
    category_code: hub.categoryCode,
    images: hub.image ? [expandAssetUrl(hub.image, assetBase)] : [],
    price_per_day: hub.pricePerDay,
    // 기간 요금 상품 — 서버가 미리 계산한 고객가 표만(원본 요금·요율 없음, 10-16)
    ...(hub.periodTotals
      ? {
          policyPeriodTotals: Object.fromEntries(hub.periodTotals),
          bundleDays: hub.bundleDays ?? null,
        }
      : {}),
    currency: hub.currency,
    created_at: "",
    updated_at: "",
    markupApplied: true,
    // 시간대 상품 — 기간 총액 계산(campingPrice)이 빼고, 카드는 대표가 "~" 만 그린다
    ...(hub.timeslot ? { rental_unit: "timeslot" as const } : {}),
    tags: hub.classKey === "unknown" ? [] : [`bike-class:${hub.classKey}`],
    day_prices: hub.tiers.map(([minDays, price]) => ({
      sub_display_order: 0,
      min_days: minDays,
      price_per_day: price,
    })),
    branches,
    // 기본 상품 카드의 지역 줄("도쿄도 외 N곳") — 일반 검색(extractBranchLocations)과 같은
    // 시·도 단위. 중복 제거·"외 N곳"은 카드(formatCityList)가 한다.
    branch_locations: regions,
  };
}

/** `shortenAssetUrl` 의 역 — 절대 URL(`http…`)은 그대로 둔다. */
export function expandAssetUrl(url: string, assetBase: string): string {
  return url.startsWith("http") ? url : `${assetBase}${url}`;
}

/**
 * 서버 데이터셋 → 화면 집계. 상품 배열에서 지점을 **역산하지 않는다** — 지점은
 * 이미 서버가 이름·주소·사진까지 붙여 보냈으므로 그대로 쓰고, 모델만 기존
 * 순수 함수(`aggregateModels`)로 묶는다.
 */
export function buildStationHubDataFromDataset(
  dataset: StationHubDataset,
  locale = "en",
): StationHubAggregate & { details: Map<string, StationDetail> } {
  const assetBase = dataset.assetBase ?? "";

  // 좌표가 없는 지점은 핀도 목록도 그릴 수 없다 — 아예 빼고 시작한다.
  const usable = dataset.stations.filter(
    (s) => s.lat != null && s.lng != null,
  );

  // 지점→상품 인덱스를 뒤집어 상품별 지점 목록(= Product.branches)을 만든다.
  // `aggregateModels` 가 모델의 지점 합집합을 이걸로 구한다.
  const branchesByProduct = new Map<
    number,
    Array<{ id: string; city: string; latitude: number; longitude: number }>
  >();
  const regionsByProduct = new Map<number, string[]>();
  for (const station of usable) {
    const branch = {
      id: station.id,
      city: station.city ?? "",
      latitude: station.lat!,
      longitude: station.lng!,
    };
    const region = station.stateProvince || station.city || "";
    for (const index of station.productIdx) {
      const list = branchesByProduct.get(index) ?? [];
      list.push(branch);
      branchesByProduct.set(index, list);
      if (region) {
        const regions = regionsByProduct.get(index) ?? [];
        regions.push(region);
        regionsByProduct.set(index, regions);
      }
    }
  }

  const productByIndex = dataset.products.map((hub, index) =>
    hubProductToProduct(
      hub,
      assetBase,
      branchesByProduct.get(index) ?? [],
      regionsByProduct.get(index) ?? [],
    ),
  );

  const stations: StationSummary[] = [];
  const details = new Map<string, StationDetail>();
  for (const station of usable) {
    const products = station.productIdx
      .map((index) => productByIndex[index])
      .filter((p): p is Product => !!p);
    const imageUrl = station.imageUrl
      ? expandAssetUrl(station.imageUrl, assetBase)
      : null;
    stations.push({
      id: station.id,
      lat: station.lat!,
      lng: station.lng!,
      city: station.city ?? "",
      products,
      minPrice: station.minPrice,
      currency: station.currency,
      classes: station.classes
        .map((key) => getBikeClass(key))
        .filter((c): c is NonNullable<ReturnType<typeof getBikeClass>> => !!c),
      image: imageUrl ?? products[0]?.images?.[0] ?? null,
    });
    details.set(station.id, { ...station, imageUrl });
  }

  // 어느 지점에도 안 걸린 상품은 허브에서 볼 방법이 없으므로 모델에서도 뺀다.
  const live = productByIndex.filter((_, index) => branchesByProduct.has(index));

  const facets = new Map<string, ProductFacets>(
    dataset.products.map((hub) => [
      hub.id,
      {
        classKey: hub.classKey,
        maker: hub.maker ?? null,
        // 옛 캐시 엔트리(makerKey 없음)면 제원 maker → 현지화 제목으로 판별한다.
        makerKey: hub.makerKey ?? resolveBikeMaker(hub.maker, hub.name),
        bikeType: hub.bikeType ?? null,
        transmission: hub.transmission ?? null,
        rideStyles: hub.rideStyles ?? [],
        partySize: hub.partySize ?? null,
      },
    ]),
  );

  return {
    stations,
    stationsById: new Map(stations.map((s) => [s.id, s])),
    models: aggregateModels(live, locale),
    facets,
    shippable: new Set(dataset.products.filter((hub) => hub.ships).map((hub) => hub.id)),
    details,
  };
}
